import { Auth } from 'aws-amplify';
import { Lambda } from '@aws-sdk/client-lambda';
import { useEffect, useState } from 'react';
import { getFeatureFlagLambdaArn } from '../config/featureFlagConfig';

// Feature flags for FinSpace website
export enum FeatureAccess {
  ENABLE_FEATURE_FLAG_TESTING = 'enable_feature_flag_testing',
  EXPERIMENTAL_MODE = 'experimental_mode',
  AI_ASSISTANT = 'ai_assistant',
}

/** Map of feature flags to their enabled state */
export type FeatureAccessMap = Record<FeatureAccess, boolean>;

/** Default feature flag values. All flags disabled by default for safety. */
export const placeholderData: FeatureAccessMap = {
  [FeatureAccess.ENABLE_FEATURE_FLAG_TESTING]: false,
  [FeatureAccess.EXPERIMENTAL_MODE]: false,
  [FeatureAccess.AI_ASSISTANT]: false,
};

/** Creates authenticated Lambda client for feature flag service */
const getAppConfigAgentLambdaClient = (region: string): Promise<Lambda> =>
  Auth.currentCredentials().then(
    (credentials) => new Lambda({ region, credentials }),
  );

/** Fetches feature flags from Lambda. Caches results for session. */
export const getFeatureAccessControls = (() => {
  let cache: FeatureAccessMap | undefined = undefined;
  
  const fetchFlags = async (invalidateCache?: boolean): Promise<FeatureAccessMap> => {
    if (cache === undefined || invalidateCache) {
      // Localhost override - enable all flags for local development
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocalhost) {
        console.log('[FeatureFlags] Localhost detected, enabling all flags');
        cache = Object.fromEntries(
          Object.values(FeatureAccess).map(flag => [flag, true])
        ) as FeatureAccessMap;
        return cache;
      }

      try {
        console.log('[FeatureFlags] Fetching feature flags from Lambda...');
        // Get stage and region from webpack templates (replaced at build time)
        const stage = 'gamma';
        const region = 'us-west-2';
        const client: Lambda = await getAppConfigAgentLambdaClient(region);
        const user = await Auth.currentAuthenticatedUser();
        // Extract alias after underscore (e.g., 'AmazonFederate_reddypn' -> 'reddypn')
        const userAlias = user.username.split('_').pop() || user.username;
        console.log('[FeatureFlags] User alias:', userAlias);
        
        const functionArn = getFeatureFlagLambdaArn(stage, region);
        console.log('[FeatureFlags] Stage:', stage, 'Region:', region);
        console.log('[FeatureFlags] Calling Lambda function:', functionArn);
        
        const encoder = new TextEncoder();

        cache = await client
          .invoke({
            FunctionName: functionArn,
            Payload: encoder.encode(
              JSON.stringify({
                context: {
                  userId: userAlias,
                },
              }),
            ),
          })
          .then((response) => {
            const decoder = new TextDecoder();
            const responseBody = JSON.parse(decoder.decode(response.Payload));
            
            const updatedFeatures: FeatureAccessMap = { ...placeholderData };

            // Update flag values only for features that exist in lambda response
            Object.values(FeatureAccess).forEach((feature) => {
              if (responseBody.body?.[feature]?.enabled !== undefined) {
                updatedFeatures[feature] = responseBody.body[feature].enabled;
              }
            });
            
            console.log('[FeatureFlags] Final feature map:', updatedFeatures);
            return updatedFeatures;
          });
      } catch (error) {
        console.error('[FeatureFlags] Error fetching feature flags:', error);
        console.log('[FeatureFlags] Using placeholder data due to error');
        cache = placeholderData;
      }
    }
    return cache ?? placeholderData;
  };
  
  // Expose cache reset for testing
  fetchFlags._resetCache = () => { cache = undefined; };
  
  return fetchFlags;
})();

/**
 * React hook to get feature flag configurations
 */
export const useFeatureAccessControls = () => {
  const [featureAccessMap, setFeatureAccessMap] = useState<FeatureAccessMap>(placeholderData);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getFeatureAccessControls()
      .then(setFeatureAccessMap)
      .finally(() => setIsLoading(false));
  }, []);

  return { data: featureAccessMap, isLoading };
};
